import { beginMessage } from '../message';
import { MessageTag } from '../messageTags';
import type { Role } from '../enums';
import { BeadColor } from '../../game/backgammon/constants';
import type { MovePacket } from '../../game/backgammon/types';
import type { BinaryReader } from '../BinaryReader';
import {
  readBackgammonLogic,
  readDicePack,
  readMovePacket,
  writeMovePacket,
  readTimeBlock,
  type BackgammonGameState,
  type RollDicePayload,
  type MoveEchoPayload,
  type SwitchTurnPayload,
} from '../models/backgammonGame';
import { decodeGameInfoPayload, type GameInfoPayload } from './matchmaking';

/**
 * Backgammon in-game codec. Mirrors Server/GameStars.Server/Games/Backgammon.cs (the authoritative
 * NetDataWriter order) and the shared engine serializers. Field order below IS the wire order — never
 * reorder; add a golden byte test for any change (see src/net/__tests__/backgammon.codec.test.ts).
 */

// ---- C2S (encode) ----

/** Move(29): int count, then each MovePacket (int hop-count, then SingleMoves). */
export function encodeMove(packets: MovePacket[]): Uint8Array {
  const w = beginMessage(MessageTag.Move);
  w.putInt(packets.length);
  for (const mp of packets) {
    writeMovePacket(w, mp);
  }
  return w.toUint8Array();
}

/** RollDice(49): empty body — the server rolls and replies with the DicePack. */
export function encodeRollDice(): Uint8Array {
  return beginMessage(MessageTag.RollDice).toUint8Array();
}

/** Undo(60): empty body — undo my last move this turn. */
export function encodeUndo(): Uint8Array {
  return beginMessage(MessageTag.Undo).toUint8Array();
}

/** Refresh(52): empty body — ask the server to re-send GetGame (full resync; shows a "refreshed" toast). */
export function encodeRefresh(): Uint8Array {
  return beginMessage(MessageTag.Refresh).toUint8Array();
}

/** GetGame(28): empty body — fetch the full board (used after StartGamePanel to pull initial state). */
export function encodeGetGame(): Uint8Array {
  return beginMessage(MessageTag.GetGame).toUint8Array();
}

/** GameAlive(59): empty body — keep-alive / time poll. */
export function encodeGameAlive(): Uint8Array {
  return beginMessage(MessageTag.GameAlive).toUint8Array();
}

// ---- S2C (decode) ----

/** GetGame(28) / GamePreview(65): BackgammonLogic, short P1Win, short P2Win, short turn, short turnColor, time. */
export function decodeGetGame(r: BinaryReader): BackgammonGameState {
  return {
    logic: readBackgammonLogic(r),
    player1TotalWin: r.getShort(),
    player2TotalWin: r.getShort(),
    turn: r.getShort() as Role,
    turnColor: r.getShort() as BeadColor,
    time: readTimeBlock(r),
  };
}

/** RollDice(49): DicePack, time. */
export function decodeRollDice(r: BinaryReader): RollDicePayload {
  return {
    dice: readDicePack(r),
    time: readTimeBlock(r),
  };
}

/** Move(29) echo: int count, MovePacket[], short P1Win, short P2Win, time. */
export function decodeMoveEcho(r: BinaryReader): MoveEchoPayload {
  const count = r.getInt();
  const moves: MovePacket[] = new Array(count);
  for (let i = 0; i < count; i++) {
    moves[i] = readMovePacket(r);
  }
  return {
    moves,
    player1TotalWin: r.getShort(),
    player2TotalWin: r.getShort(),
    time: readTimeBlock(r),
  };
}

/** SwitchTurn(70): short P1Win, short P2Win, time. */
export function decodeSwitchTurn(r: BinaryReader): SwitchTurnPayload {
  return {
    player1TotalWin: r.getShort(),
    player2TotalWin: r.getShort(),
    time: readTimeBlock(r),
  };
}

/** StartGamePanel(54): WriteGameInfo payload, then short winner, ushort winnerDice, ushort loserDice. */
export interface StartGamePanelPayload {
  info: GameInfoPayload;
  winner: Role;
  winnerDice: number;
  loserDice: number;
}

export function decodeStartGamePanel(r: BinaryReader): StartGamePanelPayload {
  return {
    info: decodeGameInfoPayload(r),
    winner: r.getShort() as Role,
    winnerDice: r.getUShort(),
    loserDice: r.getUShort(),
  };
}
